import {
  type MtmYtmRequestPayload,
  type KpiPfyPayload,
  type PlannedYearlyTechnicalCementPowerConsumptionsHierarchy,
  type TechnicalCementPowerConsumptionResponse,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {useUpmPlantsContext} from '@hconnect/upm'
import {useQueries, UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback, useMemo} from 'react'

import {mapTechnicalCementPowerConsumptionDto} from '../../mappers'
import {type KpiHookReturn} from '../../types'

import {
  getTechnicalCementPowerConsumption,
  getTechnicalCementPowerConsumptionMtmYtmQueryKeys
} from './useTechnicalCementPowerConsumption'

export const getTechnicalCementPowerConsumptionPfy = async (
  payload?: KpiPfyPayload,
  signal?: AbortSignal
) => {
  const url = '/kpi/dashboard/technical-cement-power-consumption-pfy'
  const response =
    await ApiClient.axiosInstance.get<PlannedYearlyTechnicalCementPowerConsumptionsHierarchy>(url, {
      params: {...payload},
      signal
    })

  return response.data
}

export const useTechnicalCementPowerConsumptionData = (
  payload: MtmYtmRequestPayload,
  enabled = true
): KpiHookReturn => {
  const upmPlants = useUpmPlantsContext()

  const queries = useQueries<
    [
      UseQueryResult<PlannedYearlyTechnicalCementPowerConsumptionsHierarchy, AxiosError>,
      UseQueryResult<TechnicalCementPowerConsumptionResponse, AxiosError>
    ]
  >({
    queries: [
      {
        queryKey: [
          'technical-cement-power-consumption-pfy',
          {locationId: payload?.locationId, year: payload?.year}
        ],
        queryFn: ({signal}) =>
          getTechnicalCementPowerConsumptionPfy(
            {
              locationId: payload?.locationId,
              year: payload?.year
            },
            signal
          ),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      },
      {
        queryKey: getTechnicalCementPowerConsumptionMtmYtmQueryKeys(payload),
        queryFn: ({signal}) =>
          getTechnicalCementPowerConsumption<TechnicalCementPowerConsumptionResponse>(
            payload,
            signal
          ),
        enabled,
        staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME
      }
    ]
  })

  const refetch = useCallback(() => {
    queries.forEach(({error, refetch: queryRefetch}) => {
      if (error) {
        void queryRefetch()
      }
    })
  }, [queries])

  const [pfyQuery, dataQuery] = queries

  const technicalCementPowerConsumption = useMemo(() => {
    const pfyData = pfyQuery.data
    const data = dataQuery.data
    return pfyData && data
      ? mapTechnicalCementPowerConsumptionDto(data, upmPlants, pfyData)
      : undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pfyQuery.data), JSON.stringify(dataQuery.data)])

  return {
    data: technicalCementPowerConsumption,
    isInitialLoading: pfyQuery.isInitialLoading || dataQuery.isInitialLoading,
    error: pfyQuery.error || dataQuery.error,
    refetch
  }
}
